import type { KeyboardEvent, MouseEvent, ReactNode, Ref } from 'react';
import type { AsyncSelectorMode } from './AsyncSelector.types';
import type { ColumnDef } from '../types';
import {
  SelectorCloseIcon,
  SelectorEditIcon,
  SelectorKebabIcon,
  SelectorPlusIcon,
} from './selectorIcons';

export interface AsyncSelectorFieldProps<TItem> {
  /** Ref na kořenový combobox — návrat fokusu po zavření popoveru přes ESC. */
  triggerRef?: Ref<HTMLDivElement>;
  mode: AsyncSelectorMode;
  fieldId: string;
  popupId: string;
  open: boolean;
  selected: readonly TItem[];
  columns: readonly ColumnDef<TItem>[];
  renderSelected?: (item: TItem) => ReactNode;
  renderSelectedSecondary?: (item: TItem) => ReactNode;
  getKey: (item: TItem) => string | number;
  placeholder: string;
  isPending: boolean;
  /** Jen ke čtení — pole se nedá aktivovat ani z něj odebírat, akce se skryjí. */
  disabled?: boolean;
  showCreateBtn: boolean;
  showListBtn: boolean;
  onActivate: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  onCreate: () => void;
  onOpenList: () => void;
  onEditItem?: (item: TItem) => void;
  onRemove: (item: TItem) => void;
  onClearAll: () => void;
  createLabel: string;
  openListLabel: string;
  editLabel: string;
  removeLabel: string;
  clearAllLabel: string;
  ariaLabel: string;
  ariaDescribedBy?: string;
  ariaInvalid?: boolean;
}

/** Tooltip řádku: ID + textové hodnoty všech sloupců (design: „tooltip nese ID + všechny sloupce"). */
function listRowTitle<TItem>(
  item: TItem,
  getKey: (item: TItem) => string | number,
  columns: readonly ColumnDef<TItem>[]
): string {
  const textValues = columns
    .map((col) => col.cell(item))
    .filter(
      (value): value is string | number => typeof value === 'string' || typeof value === 'number'
    );
  const idPart = `ID ${getKey(item)}`;
  return textValues.length > 0 ? `${idPart} — ${textValues.join(' · ')}` : idPart;
}

/**
 * Pole selectoru v „rows" layoutu dle designového handoffu: vybrané záznamy
 * se vykreslují jako plnohodnotné řádky uvnitř pole (první záznam nahrazuje
 * řádek s placeholderem, pole drží výšku běžného inputu). Vyhledávání probíhá
 * až v popoveru — pole samo žádný input nemá.
 */
export function AsyncSelectorField<TItem>({
  triggerRef,
  mode,
  fieldId,
  popupId,
  open,
  selected,
  columns,
  renderSelected,
  renderSelectedSecondary,
  getKey,
  placeholder,
  isPending,
  disabled = false,
  showCreateBtn,
  showListBtn,
  onActivate,
  onKeyDown,
  onCreate,
  onOpenList,
  onEditItem,
  onRemove,
  onClearAll,
  createLabel,
  openListLabel,
  editLabel,
  removeLabel,
  clearAllLabel,
  ariaLabel,
  ariaDescribedBy,
  ariaInvalid,
}: AsyncSelectorFieldProps<TItem>) {
  const filled = selected.length > 0;
  const showClearAll = mode === 'multi' && selected.length >= 2;

  const renderPrimary = (item: TItem): ReactNode =>
    renderSelected ? renderSelected(item) : (columns[0]?.cell(item) ?? String(item));

  // Sekundární text: explicitní renderer, jinak 2. sloupec (jen pokud primární
  // text není custom render — ten už typicky nese celé zobrazení záznamu).
  const renderSecondary = (item: TItem): ReactNode => {
    if (renderSelectedSecondary) return renderSelectedSecondary(item);
    if (renderSelected) return null;
    return columns[1]?.cell(item) ?? null;
  };

  const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (disabled) return;
    if ((event.target as HTMLElement).closest('button')) return;
    event.preventDefault();
    onActivate();
  };

  return (
    <div
      ref={triggerRef}
      className="phxsel-field"
      id={fieldId}
      role="combobox"
      // Zamčené pole z tab pořadí vypadává — je to jen text, není v něm co ovládat.
      tabIndex={disabled ? -1 : 0}
      aria-expanded={open}
      aria-haspopup="listbox"
      aria-controls={popupId}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid || undefined}
      aria-disabled={disabled || undefined}
      onMouseDown={handleMouseDown}
      onKeyDown={onKeyDown}
    >
      <div className="phxsel-rowscol">
        {filled ? (
          <div className="phxsel-rows">
            {selected.map((item) => {
              const secondary = renderSecondary(item);
              return (
                <div
                  key={getKey(item)}
                  className="phxsel-listrow"
                  title={listRowTitle(item, getKey, columns)}
                  data-testid="async-selector-listrow"
                >
                  <span className="phxsel-listrow-text">
                    <span className="phxsel-listrow-pri">{renderPrimary(item)}</span>
                    {secondary != null && secondary !== '' ? (
                      <span className="phxsel-listrow-sec">{secondary}</span>
                    ) : null}
                  </span>
                  <span className="phxsel-listrow-acts" hidden={disabled}>
                    {onEditItem ? (
                      <button
                        type="button"
                        className="phxsel-listrow-btn"
                        title={editLabel}
                        aria-label={editLabel}
                        tabIndex={-1}
                        disabled={isPending}
                        onClick={(event) => {
                          event.stopPropagation();
                          onEditItem(item);
                        }}
                      >
                        <SelectorEditIcon />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="phxsel-listrow-btn"
                      data-x=""
                      title={removeLabel}
                      aria-label={removeLabel}
                      tabIndex={-1}
                      disabled={isPending}
                      onClick={(event) => {
                        event.stopPropagation();
                        onRemove(item);
                      }}
                    >
                      <SelectorCloseIcon size={12.5} />
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="phxsel-placeholder">{placeholder}</div>
        )}
      </div>

      {showCreateBtn || showListBtn || showClearAll ? (
        <div className="phxsel-acts">
          <div className="phxsel-acts-top">
            {showCreateBtn ? (
              <button
                type="button"
                className="phxsel-act"
                title={createLabel}
                aria-label={createLabel}
                disabled={isPending}
                onClick={(event) => {
                  event.stopPropagation();
                  onCreate();
                }}
              >
                <SelectorPlusIcon />
              </button>
            ) : null}
            {showListBtn ? (
              <button
                type="button"
                className="phxsel-act"
                title={openListLabel}
                aria-label={openListLabel}
                disabled={isPending}
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenList();
                }}
              >
                <SelectorKebabIcon />
              </button>
            ) : null}
          </div>
          {showClearAll ? (
            <button
              type="button"
              className="phxsel-act phxsel-clearall"
              data-x=""
              title={clearAllLabel}
              aria-label={clearAllLabel}
              disabled={isPending}
              onClick={(event) => {
                event.stopPropagation();
                onClearAll();
              }}
            >
              <SelectorCloseIcon size={12.5} />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
